import { Link, Stack, Typography } from '@mui/material';
import { META } from 'common/constants';
import type { FC } from 'react';

const ElectronicDeliveryConsent: FC = () => {
  return (
    <Stack
      minHeight="100vh"
      direction="row"
      justifyContent="center"
      sx={{ bgcolor: (theme) => theme.palette.customColors.softWhite }}
      py={8}
      px={15}
    >
      <Stack direction="column" maxWidth={1200}>
        <Typography variant="h1" pb={3}>
          Electronic delivery consent
        </Typography>

        <Stack
          direction="column"
          gap={2}
          sx={{ bgcolor: (theme) => theme.palette.customColors.white50, p: 4 }}
        >
          <Typography>
            By selecting the paperless option, you are consenting that we may send an email to the
            address below when a policy or other document is available to access on our website.
            From time to time we will send important materials, including the policy, and other
            important notices.
          </Typography>

          <Typography>
            Unless required by law, all notices, disclosures, authorizations, acknowledgements and
            other documents relating to any insurance policy may be provided the same way. We may
            still send some notices in paper form at the regular mailing address on the policy.
          </Typography>

          <Typography>
            The right to terminate coverage and receive a complete refund and our right to contest a
            claim based on statements in the application may be dependent on when the policy (or
            other related information) is delivered.
          </Typography>

          <Typography>
            For purposes of determining when any correspondence that we send electronically was
            received, you acknowledge that your business is considered to have received the policy
            and other related information when we email the address provided below that the policy
            or other information is available for access on our website. Some information to be
            provided may be in pdf format. You agree that you can open materials sent in pdf format.
            If you are not certain, click{' '}
            <Link underline="none" href="https://get.adobe.com/reader/" target="_blank">
              https://get.adobe.com/reader/
            </Link>{' '}
            here to access.
          </Typography>

          <Typography>
            The computer hardware and software used to access our website via the Internet is all
            you will need to access your business' insurance policy, related notices, disclosures,
            authorizations, acknowledgements and other documents provided in electronic form. To
            retain a copy of these materials, you may print copies of such materials provided or
            email a copy to an address where you can print on paper or save it for your future
            reference.
          </Typography>

          <Typography>
            You may also save an electronic copy, in which case you may need up to 50,000 bytes per
            page of space to store such materials. You may access this information at any time by
            logging into our website.
          </Typography>

          <Typography>
            If you wish to change the e-mail address where we send the notices and disclosures to
            you, you must either e-mail us at{' '}
            <Link
              href={`mailto:${META.SERVICE_EMAIL}`}
              underline="none"
              sx={{ color: (theme) => theme.palette.customColors.copper }}
            >
              {META.SERVICE_EMAIL}
            </Link>{' '}
            or write to us at THREE, PO Box 1668 Wilkes Barre, PA 18703 and in the body of such
            request provide your previous e-mail address, your new e-mail address, and policy
            number. We may require reasonable verification before making any such change effective.
          </Typography>

          <Typography>
            To withdraw consent to receive future notices and disclosures in electronic form as
            described above, you must either e-mail us at{' '}
            <Link
              href={`mailto:${META.SERVICE_EMAIL}`}
              underline="none"
              sx={{ color: (theme) => theme.palette.customColors.copper }}
            >
              {META.SERVICE_EMAIL}
            </Link>{' '}
            or write to us at THREE, PO Box 1668 Wilkes Barre, PA 18703 and in the body of such
            request provide your e-mail, full name, US Postal address, telephone number, and policy
            number. We may require reasonable verification before making any such change effective.
          </Typography>

          <Typography>
            If you would like to receive a paper copy of the materials delivered via the website,
            you may contact us at{' '}
            <Link underline="none" href={`tel:${META.CONTACT_NUMBER}`}>
              {META.CONTACT_NUMBER}
            </Link>{' '}
            and request a copy. If you have trouble accessing the website to retrieve information to
            be provided in this way, you may contact us at{' '}
            <Link underline="none" href={`tel:${META.CONTACT_NUMBER}`}>
              {META.CONTACT_NUMBER}
            </Link>
            .
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default ElectronicDeliveryConsent;
